// src/pages/division/StatsDashboard.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { GlobalStyles } from "../../constants/styles";
import PaginatedFlatTable from "../pagination/PaginatedFlatTable";
import {
    SOCCER_ACCUMULATED_YELLOW_CARDS,
    SOCCER_LEADERS,
    SOCCER_MOST_GAMES_PLAYED,
} from "./queries/SoccerTopTenScorer";
import { SEASON_PLAYER_LEADERBOARDS } from "./queries/SportLeaders";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

function Avatar({ src, alt, size = 28 }) {
    const fallback = (alt || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("");

    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: 999,
                overflow: "hidden",
                background: GlobalStyles.colors.canary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                fontSize: Math.max(11, Math.round(size * 0.42)),
                color: GlobalStyles.colors.woodsmoke,
                flex: "0 0 auto",
                border: `1px solid ${GlobalStyles.colors.woodsmoke}`,
            }}
            title={alt}
            aria-label={alt}
        >
            {src ? (
                <img
                    src={src}
                    alt={alt}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                        e.currentTarget.style.display = "none";
                    }}
                />
            ) : (
                fallback || "?"
            )}
        </div>
    );
}

function PillTabs({ value, onChange, items, ui, ariaLabel, unavailableLabel }) {
    return (
        <div style={ui.pillRow} role="tablist" aria-label={ariaLabel}>
            {items.map((it) => {
                const active = it.id === value;
                const disabled = !!it.disabled;

                return (
                    <button
                        key={it.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={disabled}
                        onClick={() => !disabled && onChange(it.id)}
                        className="kbola-pill"
                        style={{
                            ...ui.pill,
                            ...(active ? ui.pillActive : null),
                            ...(disabled ? ui.pillDisabled : null),
                            cursor: disabled ? "not-allowed" : "pointer",
                        }}
                        title={disabled ? it.disabledReason || unavailableLabel : it.label}
                    >
                        {it.icon ? <i className={`fas ${it.icon}`} /> : null}
                        {it.label}
                    </button>
                );
            })}
        </div>
    );
}

function StatCard({ title, icon, right, children, ui }) {
    return (
        <div style={ui.card}>
            <div style={ui.cardHeader}>
                <div style={ui.cardTitle}>
                    {icon ? <i className={`fas ${icon}`} style={{ opacity: 0.8 }} /> : null}
                    <span>{title}</span>
                </div>
                {right ? <div style={ui.cardRight}>{right}</div> : null}
            </div>
            <div style={ui.cardBody}>{children}</div>
        </div>
    );
}

function TopTenList({
                        rows,
                        loading,
                        error,
                        ui,
                        valueLabel,
                        metricKey = "value",
                        metricIconClass = null,
                        metricIconColor = null,
                        loadingLabel,
                        failedLabel,
                        emptyLabel,
                        emptyValueLabel,
                    }) {
    if (loading) return <div style={ui.muted}>{loadingLabel}</div>;
    if (error) return <div style={ui.error}>{failedLabel}</div>;
    if (!rows?.length) return <div style={ui.muted}>{emptyLabel}</div>;

    return (
        <div style={{ display: "grid", gap: 8 }}>
            {rows.map((r, idx) => {
                const metric = r?.[metricKey] ?? 0;

                return (
                    <div key={`${r.player?.id || idx}_${idx}`} style={ui.row}>
                        <div style={ui.rank}>{idx + 1}</div>

                        <div style={ui.rowMain}>
                            <div style={ui.person}>
                                <Avatar src={r.player?.logoUrl} alt={r.player?.fullName} size={50} />

                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={ui.personName} title={r.player?.fullName}>
                                        {r.player?.fullName || emptyValueLabel}
                                    </div>

                                    <div style={ui.personSub} title={r.team?.name}>
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                            <Avatar src={r.team?.logoUrl} alt={r.team?.name} size={25} />
                                            <span style={ui.teamName}>{r.team?.name || emptyValueLabel}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div style={ui.metricWrap}>
                                {metricIconClass ? (
                                    <i
                                        className={`fas ${metricIconClass}`}
                                        aria-hidden="true"
                                        style={{
                                            color: metricIconColor || "rgba(20,21,26,0.70)",
                                            fontSize: 16,
                                            opacity: 0.95,
                                        }}
                                        title={valueLabel}
                                    />
                                ) : null}

                                <div style={{ textAlign: "right" }}>
                                    <div style={ui.metricNum}>{metric}</div>
                                    <div style={ui.metricLbl}>{valueLabel}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function StatsDashboard({ sportKey, seasonId, stageId = null, isActive, pro }) {
    const [section, setSection] = useState("leaders");
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [cardsPage, setCardsPage] = useState(1);
    const [gamesPage, setGamesPage] = useState(1);

    const cardsPageSize = 25;
    const gamesPageSize = 25;

    useEffect(() => {
        setCardsPage(1);
        setGamesPage(1);
    }, [seasonId, stageId, section]);

    useEffect(() => {
        setSection("leaders");
    }, [sportKey]);

    const scopeId = useMemo(() => {
        const safe = `stats-${sportKey || "sport"}-${seasonId || "x"}-${stageId || "all"}`
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `kbola-${safe}`;
    }, [sportKey, seasonId, stageId]);

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
                overflow: "hidden",
            },
            header: {
                padding: "12px 12px 10px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            },
            title: {
                margin: 0,
                fontSize: 13,
                fontWeight: 900,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: colors.textOnPrimary,
            },
            sub: {
                margin: "6px 0 0",
                fontSize: 12,
                color: colors.textOnPrimary,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.secondary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnSecondary,
                whiteSpace: "nowrap",
            },
            body: { padding: 14 },

            pillRow: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 },
            pill: {
                borderRadius: 999,
                padding: "8px 12px",
                fontWeight: 900,
                fontSize: 13,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                color: "rgba(20,21,26,0.75)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
            },
            pillActive: {
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.12)",
                color: colors.textOnPrimary,
                boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
            },
            pillDisabled: { opacity: 0.55, background: "rgba(0,0,0,0.03)" },

            grid: { display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 12 },

            card: {
                borderRadius: 16,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
                overflow: "hidden",
                minWidth: 0,
            },
            cardHeader: {
                padding: "10px 12px",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                background: colors.primary,
            },
            cardTitle: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontWeight: 900,
                fontSize: 15,
                color: colors.textOnPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.02em",
            },
            cardRight: { fontSize: 12, color: colors.textOnPrimary },
            cardBody: { padding: 12 },

            row: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 10px",
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
                minWidth: 0,
            },
            rank: {
                width: 26,
                height: 26,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                fontSize: 12,
                background: GlobalStyles.colors.white,
                border: `1px solid ${GlobalStyles.colors.woodsmoke}`,
                color: GlobalStyles.colors.woodsmoke,
                flex: "0 0 auto",
            },
            rowMain: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                width: "100%",
                minWidth: 0,
            },
            person: { display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 },
            personName: {
                fontWeight: 900,
                fontSize: 13,
                color: "rgba(20,21,26,0.85)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
            },
            personSub: { fontSize: 12, color: "rgba(20,21,26,0.55)", minWidth: 0 },
            teamName: {
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
                maxWidth: 180,
            },

            metricWrap: {
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                flex: "0 0 auto",
            },
            metricNum: { fontWeight: 1000, fontSize: 18, color: colors.woodsmoke, lineHeight: 1.1 },
            metricLbl: { fontSize: 11, color: "rgba(20,21,26,0.55)", fontWeight: 900, textTransform: "uppercase" },

            muted: { color: colors.textOnPrimary, fontSize: 13 },
            error: { color: "#b42318", fontSize: 13, fontWeight: 800 },

            lock: {
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(0,0,0,0.02)",
                padding: 12,
                color: "rgba(20,21,26,0.70)",
            },
        };
    }, [colors.primary, colors.secondary, colors.textOnPrimary, colors.textOnSecondary, colors.woodsmoke]);

    const sections = useMemo(
        () => [
            { id: "leaders", label: t("statsDashboard.sections.leaders"), icon: "fa-trophy" },
            ...(sportKey === "soccer"
                ? [{ id: "cards", label: t("statsDashboard.sections.cards"), icon: "fa-id-card" }]
                : []),
            { id: "gamesPlayed", label: t("statsDashboard.sections.gamesPlayed"), icon: "fa-running" },
        ],
        [sportKey, t]
    );

    const usesSportSpecificLeaderboards = ["hockey", "baseball", "softball"].includes(sportKey);
    const shouldFetchLeaders = Boolean(
        isActive && pro && section === "leaders" && seasonId && !usesSportSpecificLeaderboards
    );

    const {
        data: leadersData,
        loading: leadersLoading,
        error: leadersError,
        refetch: refetchLeaders,
    } = useQuery(SOCCER_LEADERS, {
        variables: { seasonId, stageId, limit: 10 },
        skip: !shouldFetchLeaders,
        fetchPolicy: "cache-first",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });

    const leaders = leadersData?.soccerLeaders;
    const topScorers = leaders?.topScorers || [];
    const topAssists = leaders?.topAssists || [];
    const topRedCards = leaders?.topRedCards || [];
    const topGoalContributions = leaders?.topGoalContributions || [];
    const topOwnGoals = leaders?.topOwnGoals || [];

    const shouldFetchSportLeaderboards = Boolean(
        isActive && pro && section === "leaders" && seasonId && usesSportSpecificLeaderboards
    );
    const {
        data: sportLeadersData,
        loading: sportLeadersLoading,
        error: sportLeadersError,
        refetch: refetchSportLeaders,
    } = useQuery(SEASON_PLAYER_LEADERBOARDS, {
        variables: {
            seasonId: parseInt(seasonId, 10),
            sportKey,
            stageId: stageId ? parseInt(stageId, 10) : null,
            limit: 10,
        },
        skip: !shouldFetchSportLeaderboards,
        fetchPolicy: "cache-first",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });
    const sportLeaderboards = sportLeadersData?.seasonPlayerLeaderboards || [];

    const shouldFetchCards = Boolean(
        isActive && pro && section === "cards" && seasonId && sportKey === "soccer"
    );

    const {
        data: cardsData,
        loading: cardsLoading,
        error: cardsError,
    } = useQuery(SOCCER_ACCUMULATED_YELLOW_CARDS, {
        variables: {
            seasonId: parseInt(seasonId, 10),
            stageId: stageId ? parseInt(stageId, 10) : null,
            page: cardsPage,
            pageSize: cardsPageSize,
        },
        skip: !shouldFetchCards,
        fetchPolicy: "cache-first",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });

    const cardsEnvelope = cardsData?.accumulatedYellowCards;
    const yellowCardsItems = cardsEnvelope?.objects || [];
    const yellowCardsPages = cardsEnvelope?.pages || 1;

    const shouldFetchGamesPlayed = Boolean(isActive && pro && section === "gamesPlayed" && seasonId);

    const {
        data: gamesPlayedData,
        loading: gamesPlayedLoading,
        error: gamesPlayedError,
    } = useQuery(SOCCER_MOST_GAMES_PLAYED, {
        variables: {
            seasonId: parseInt(seasonId, 10),
            stageId: stageId ? parseInt(stageId, 10) : null,
            page: gamesPage,
            pageSize: gamesPageSize,
        },
        skip: !shouldFetchGamesPlayed,
        fetchPolicy: "cache-first",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });

    const gamesPlayedEnvelope = gamesPlayedData?.mostGamesPlayed;
    const gamesPlayedItems = gamesPlayedEnvelope?.objects || [];
    const gamesPlayedPages = gamesPlayedEnvelope?.pages || 1;

    return (
        <div id={scopeId} style={ui.wrap}>
            <div style={ui.header}>
                <div style={ui.titleRow}>
                    <div style={{ minWidth: 220 }}>
                        <h3 style={ui.title}>{t("statsDashboard.title")}</h3>
                        <p style={ui.sub}>
                            {t("statsDashboard.subtitle", {
                                stageSuffix: stageId ? t("statsDashboard.stageSuffix") : "",
                            })}
                        </p>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={ui.badge}>
                            {pro ? t("statsDashboard.plan.pro") : t("statsDashboard.plan.basic")}
                        </span>

                        {pro && section === "leaders" && isActive && (
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => sportKey === "soccer" ? refetchLeaders?.() : refetchSportLeaders?.()}
                                style={{
                                    borderRadius: 999,
                                    fontWeight: 900,
                                    border: "1px solid rgba(0,0,0,0.12)",
                                    background: colors.primary,
                                    color: colors.textOnPrimary,
                                }}
                            >
                                <i className="fas fa-sync-alt" style={{ marginRight: 6 }} />
                                {t("statsDashboard.actions.refresh")}
                            </button>
                        )}
                    </div>
                </div>

                <PillTabs
                    value={section}
                    onChange={setSection}
                    items={sections}
                    ui={ui}
                    ariaLabel={t("statsDashboard.tabsAria")}
                    unavailableLabel={t("statsDashboard.states.unavailable")}
                />
            </div>

            <div style={ui.body}>
                {!pro ? (
                    <div style={ui.lock}>
                        <b>{t("statsDashboard.upgrade.title")}</b> {t("statsDashboard.upgrade.description")}
                    </div>
                ) : (
                    <>
                        {section === "leaders" && (
                            usesSportSpecificLeaderboards ? (
                                <div style={ui.grid} className="kbola-stats-grid">
                                    {sportLeadersError ? (
                                        <div style={{ gridColumn: "span 12" }} className="alert alert-danger">
                                            {t("statsDashboard.states.failedToLoad")}
                                        </div>
                                    ) : sportLeaderboards.map((leaderboard) => (
                                        <div className="kbola-col" key={leaderboard.key}>
                                            <StatCard
                                                title={t(`statsDashboard.leaderCards.${leaderboard.key}`)}
                                                icon="fa-chart-line"
                                                right={<span style={ui.muted}>{t("statsDashboard.cards.top10")}</span>}
                                                ui={ui}
                                            >
                                                <TopTenList
                                                    rows={leaderboard.rows}
                                                    loading={sportLeadersLoading}
                                                    error={sportLeadersError}
                                                    ui={ui}
                                                    valueLabel={t(`statsDashboard.metrics.${leaderboard.key}`)}
                                                    loadingLabel={t("statsDashboard.states.loading")}
                                                    failedLabel={t("statsDashboard.states.failedToLoad")}
                                                    emptyLabel={t("statsDashboard.states.noData")}
                                                    emptyValueLabel={t("statsDashboard.states.empty")}
                                                />
                                            </StatCard>
                                        </div>
                                    ))}
                                    {!sportLeadersLoading && !sportLeadersError && sportLeaderboards.length === 0 && (
                                        <div style={{ gridColumn: "span 12", ...ui.muted }}>
                                            {t("statsDashboard.states.noData")}
                                        </div>
                                    )}
                                </div>
                            ) : (
                            <div style={ui.grid} className="kbola-stats-grid">
                                <div className="kbola-col">
                                    <StatCard
                                        title={t("statsDashboard.leaderCards.topScorers")}
                                        icon="fa-futbol"
                                        right={<span style={ui.muted}>{t("statsDashboard.cards.top10")}</span>}
                                        ui={ui}
                                    >
                                        <TopTenList
                                            rows={topScorers}
                                            loading={leadersLoading}
                                            error={leadersError}
                                            ui={ui}
                                            valueLabel={t("statsDashboard.metrics.goals")}
                                            metricKey="value"
                                            loadingLabel={t("statsDashboard.states.loading")}
                                            failedLabel={t("statsDashboard.states.failedToLoad")}
                                            emptyLabel={t("statsDashboard.states.noData")}
                                            emptyValueLabel={t("statsDashboard.states.empty")}
                                        />
                                    </StatCard>
                                </div>

                                <div className="kbola-col">
                                    <StatCard
                                        title={t("statsDashboard.leaderCards.topAssists")}
                                        icon="fa-hands-helping"
                                        right={<span style={ui.muted}>{t("statsDashboard.cards.top10")}</span>}
                                        ui={ui}
                                    >
                                        <TopTenList
                                            rows={topAssists}
                                            loading={leadersLoading}
                                            error={leadersError}
                                            ui={ui}
                                            valueLabel={t("statsDashboard.metrics.assists")}
                                            metricKey="value"
                                            loadingLabel={t("statsDashboard.states.loading")}
                                            failedLabel={t("statsDashboard.states.failedToLoad")}
                                            emptyLabel={t("statsDashboard.states.noData")}
                                            emptyValueLabel={t("statsDashboard.states.empty")}
                                        />
                                    </StatCard>
                                </div>

                                <div className="kbola-col">
                                    <StatCard
                                        title={t("statsDashboard.leaderCards.topRedCards")}
                                        icon="fa-id-card"
                                        right={<span style={ui.muted}>{t("statsDashboard.cards.top10")}</span>}
                                        ui={ui}
                                    >
                                        <TopTenList
                                            rows={topRedCards}
                                            loading={leadersLoading}
                                            error={leadersError}
                                            ui={ui}
                                            valueLabel={t("statsDashboard.metrics.reds")}
                                            metricKey="value"
                                            metricIconClass="fa-clone"
                                            metricIconColor="#ef4444"
                                            loadingLabel={t("statsDashboard.states.loading")}
                                            failedLabel={t("statsDashboard.states.failedToLoad")}
                                            emptyLabel={t("statsDashboard.states.noData")}
                                            emptyValueLabel={t("statsDashboard.states.empty")}
                                        />
                                    </StatCard>
                                </div>

                                <div className="kbola-col">
                                    <StatCard
                                        title={t("statsDashboard.leaderCards.topGoalContributions")}
                                        icon="fa-chart-line"
                                        right={<span style={ui.muted}>{t("statsDashboard.cards.top10")}</span>}
                                        ui={ui}
                                    >
                                        <TopTenList
                                            rows={topGoalContributions}
                                            loading={leadersLoading}
                                            error={leadersError}
                                            ui={ui}
                                            valueLabel={t("statsDashboard.metrics.goalContributions")}
                                            loadingLabel={t("statsDashboard.states.loading")}
                                            failedLabel={t("statsDashboard.states.failedToLoad")}
                                            emptyLabel={t("statsDashboard.states.noData")}
                                            emptyValueLabel={t("statsDashboard.states.empty")}
                                        />
                                    </StatCard>
                                </div>

                                <div className="kbola-col">
                                    <StatCard
                                        title={t("statsDashboard.leaderCards.topOwnGoals")}
                                        icon="fa-futbol"
                                        right={<span style={ui.muted}>{t("statsDashboard.cards.top10")}</span>}
                                        ui={ui}
                                    >
                                        <TopTenList
                                            rows={topOwnGoals}
                                            loading={leadersLoading}
                                            error={leadersError}
                                            ui={ui}
                                            valueLabel={t("statsDashboard.metrics.ownGoals")}
                                            loadingLabel={t("statsDashboard.states.loading")}
                                            failedLabel={t("statsDashboard.states.failedToLoad")}
                                            emptyLabel={t("statsDashboard.states.noData")}
                                            emptyValueLabel={t("statsDashboard.states.empty")}
                                        />
                                    </StatCard>
                                </div>
                            </div>
                            )
                        )}

                        {section === "cards" && (
                            <div style={ui.grid}>
                                <div style={{ gridColumn: "span 12" }}>
                                    {cardsError ? (
                                        <div className="alert alert-danger" style={{ borderRadius: 14 }}>
                                            {t("statsDashboard.cards.failedToLoad")}
                                        </div>
                                    ) : (
                                        <PaginatedFlatTable
                                            title={t("statsDashboard.cards.accumulatedYellowCards")}
                                            subtitle={t("statsDashboard.cards.accumulatedYellowCardsSubtitle")}
                                            page={cardsPage}
                                            pages={yellowCardsPages}
                                            items={yellowCardsItems}
                                            loading={cardsLoading}
                                            onPageChange={setCardsPage}
                                            headers={[
                                                t("statsDashboard.table.player"),
                                                t("statsDashboard.table.team"),
                                                t("statsDashboard.table.yellows"),
                                            ]}
                                            renderRow={(row) => (
                                                <tr key={row.id}>
                                                    <td>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 10,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Avatar src={row.player?.logoUrl} alt={row.player?.fullName} size={50} />
                                                            <div style={{ minWidth: 0 }}>
                                                                <div
                                                                    style={{
                                                                        fontWeight: 900,
                                                                        color: "rgba(20,21,26,0.88)",
                                                                    }}
                                                                >
                                                                    {row.player?.fullName || t("statsDashboard.states.empty")}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        fontSize: 12,
                                                                        color: "rgba(20,21,26,0.55)",
                                                                        fontWeight: 800,
                                                                    }}
                                                                >
                                                                    {stageId
                                                                        ? t("statsDashboard.cards.stageFiltered")
                                                                        : t("statsDashboard.cards.seasonTotal")}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 10,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Avatar src={row.team?.logoUrl} alt={row.team?.name} size={25} />
                                                            <div
                                                                style={{
                                                                    minWidth: 0,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {row.team?.name || t("statsDashboard.states.empty")}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className="text-right">
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: 8,
                                                                minWidth: 76,
                                                                padding: "6px 12px",
                                                                borderRadius: 999,
                                                                border: "1px solid rgba(0,0,0,0.10)",
                                                                fontWeight: 1000,
                                                                color: colors.woodsmoke,
                                                                whiteSpace: "nowrap",
                                                            }}
                                                            title={t("statsDashboard.cards.yellowsTitle", {
                                                                count: row.yellowCards ?? 0,
                                                            })}
                                                        >
                                                            <i
                                                                className="fas fa-clone"
                                                                style={{ color: GlobalStyles.colors.warning, fontSize: 14 }}
                                                                aria-hidden="true"
                                                            />
                                                            <span>{row.yellowCards ?? 0}</span>
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {section === "gamesPlayed" && (
                            <div style={ui.grid}>
                                <div style={{ gridColumn: "span 12" }}>
                                    {gamesPlayedError ? (
                                        <div className="alert alert-danger" style={{ borderRadius: 14 }}>
                                            {t("statsDashboard.gamesPlayed.failedToLoad")}
                                        </div>
                                    ) : (
                                        <PaginatedFlatTable
                                            title={t("statsDashboard.gamesPlayed.title")}
                                            subtitle={t("statsDashboard.gamesPlayed.subtitle")}
                                            page={gamesPage}
                                            pages={gamesPlayedPages}
                                            items={gamesPlayedItems}
                                            loading={gamesPlayedLoading}
                                            onPageChange={setGamesPage}
                                            headers={[
                                                t("statsDashboard.table.player"),
                                                t("statsDashboard.table.team"),
                                                t("statsDashboard.table.gamesPlayed"),
                                            ]}
                                            renderRow={(row) => (
                                                <tr key={`${row.profileId}-${row.player?.id || "player"}`}>
                                                    <td>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 10,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Avatar src={row.player?.logoUrl} alt={row.player?.fullName} size={50} />
                                                            <div style={{ minWidth: 0 }}>
                                                                <div
                                                                    style={{
                                                                        fontWeight: 900,
                                                                        color: "rgba(20,21,26,0.88)",
                                                                    }}
                                                                >
                                                                    {row.player?.fullName || t("statsDashboard.states.empty")}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        fontSize: 12,
                                                                        color: "rgba(20,21,26,0.55)",
                                                                        fontWeight: 800,
                                                                    }}
                                                                >
                                                                    {stageId
                                                                        ? t("statsDashboard.gamesPlayed.stageFiltered")
                                                                        : t("statsDashboard.gamesPlayed.seasonTotal")}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 10,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Avatar src={row.team?.logoUrl} alt={row.team?.name} size={25} />
                                                            <div
                                                                style={{
                                                                    minWidth: 0,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {row.team?.name || t("statsDashboard.states.empty")}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className="text-right">
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: 8,
                                                                minWidth: 88,
                                                                padding: "6px 12px",
                                                                borderRadius: 999,
                                                                border: "1px solid rgba(0,0,0,0.10)",
                                                                fontWeight: 1000,
                                                                color: colors.woodsmoke,
                                                                whiteSpace: "nowrap",
                                                            }}
                                                            title={t("statsDashboard.gamesPlayed.gamesTitle", {
                                                                count: row.gamesPlayed ?? 0,
                                                            })}
                                                        >
                                                            <i
                                                                className="fas fa-running"
                                                                style={{ color: colors.primary, fontSize: 14 }}
                                                                aria-hidden="true"
                                                            />
                                                            <span>{row.gamesPlayed ?? 0}</span>
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <style>{`
        #${scopeId} .kbola-pill:hover:not(:disabled){
          transform: translateY(-1px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.08);
        }

        #${scopeId} .kbola-stats-grid .kbola-col{
          grid-column: span 12;
          min-width: 0;
        }

        @media (min-width: 900px){
          #${scopeId} .kbola-stats-grid .kbola-col{
            grid-column: span 6;
          }
        }

        @media (max-width: 420px){
          #${scopeId} .kbola-stats-grid .kbola-col{
            grid-column: span 12;
          }
        }
      `}</style>
        </div>
    );
}
